/**
 * A value that is exactly ONE variable reference, or null for a plain number.
 *
 * The whole grammar — there is no arithmetic to parse, which is the point. Named generally
 * because `VariableField` reads it too: one definition of what a reference looks like, shared by
 * the thing that stores them and the control that writes them.
 */
export function soleVariableName(y) {
    if (typeof y !== 'string')
        return null;
    const m = /^\s*\{([A-Za-z][A-Za-z0-9_]*)\}\s*$/.exec(y);
    return m ? m[1] : null;
}
/** Every variable named by any point, in first-seen order. Used by the editor to ask whether each
 *  one still exists. */
export function dataVizVariableNames(series) {
    const seen = new Set();
    const out = [];
    for (const s of series) {
        for (const p of s.points) {
            const name = soleVariableName(p.y);
            if (name && !seen.has(name)) {
                seen.add(name);
                out.push(name);
            }
        }
    }
    return out;
}
/**
 * Resolve every `y` against a roll.
 *
 * A point whose reference does not resolve is DROPPED, matching how `resolveVariables` omits a
 * variable it cannot compute rather than guessing a value for it. Dropping is also the only honest
 * option for a chart: there is no "unknown" position on an axis, and a zero would be a data point
 * the teacher never authored.
 *
 * `values` holds formatted strings (that is what every roll in this app produces), so a locale with
 * a decimal comma would parse `1,5` as 1 — the caller passes raw roll values, never display text.
 */
export function resolveDataVizSeries(series, values) {
    return series.map(s => ({
        ...s,
        points: s.points.flatMap((p) => {
            if (typeof p.y === 'number')
                return [{ x: p.x, y: p.y }];
            const name = soleVariableName(p.y);
            const raw = name ? values[name] : undefined;
            const n = raw === undefined ? NaN : Number(raw);
            return Number.isFinite(n) ? [{ x: p.x, y: n }] : [];
        }),
    }));
}
/** The block's data, resolved. Threaded through so a caller never rebuilds the object by hand and
 *  forgets a field — the enumeration bug this codebase keeps paying for. */
export function resolveDataVizData(data, values) {
    return { ...data, series: resolveDataVizSeries(data.series, values) };
}
//# sourceMappingURL=dataviz.js.map